<script setup lang="ts">
import { formatIsoDate } from "~/core/utils/date";
import { NO_CONTENT, PATH_DTWINS_ID } from "~/modules/dtwin/constants";
import type { Dtwin } from "~/modules/dtwin/models/Dtwin.interface";
import type { DtwinGeolocationCoordinates } from "~/modules/dtwin/models/DtwinGeolocationCoordinates.interface";
import { getDtwinGeolocationCoordinates } from "~/modules/dtwin/utils/getDtwinGeolocationCoordinates";
import { getLifeCycleStateProperties } from "~/modules/dtwin/utils/getLifeCycleStateProperties";
import ILxcArrowRightCircle from "~icons/lxc/arrow-right-circle";

const props = defineProps<{
  dtwin: Dtwin;
  modelLabel: string;
}>();

const { t, te } = useI18n();

const dateFormat: string = t("dateFormat.datetime");

const router = useRouter();

const coordinates: ComputedRef<DtwinGeolocationCoordinates> = computed(() =>
  getDtwinGeolocationCoordinates(props.dtwin),
);

const latLongRepresentation = computed(() => {
  let value = NO_CONTENT;
  if (
    coordinates.value.latitude !== undefined &&
    coordinates.value.longitude !== undefined
  ) {
    value = t("dtwins.form.description.geolocation.attributes.latlong", {
      latitude: coordinates.value.latitude,
      longitude: coordinates.value.longitude,
    });
  }
  return value;
});
</script>

<template>
  <div class="p-4">
    <lxc-form-item
      :label="t('dtwins.form.description.idCard.attributes.type')"
      read-only
    >
      <lxc-input :value="modelLabel" type="text" />
    </lxc-form-item>
    <lxc-form-item
      :label="t('dtwins.form.description.idCard.attributes.serialNumber')"
      read-only
    >
      <lxc-input :value="dtwin.attributes.serialNumber" type="text" />
    </lxc-form-item>
    <lxc-form-item
      :label="t('dtwins.form.description.state.attributes.state')"
      read-only
    >
      <lxc-input
        :value="
          dtwin.lifeCycleState
            ? getLifeCycleStateProperties(props.dtwin?.lifeCycleState, t, te)
                .label
            : NO_CONTENT
        "
        type="text"
      />
    </lxc-form-item>
    <lxc-form-item
      :label="t('dtwins.form.description.state.attributes.lastConnection')"
      read-only
    >
      <lxc-input
        :value="
          dtwin.lastConnectionDate
            ? formatIsoDate(props.dtwin.lastConnectionDate, dateFormat)
            : NO_CONTENT
        "
        type="text"
      />
    </lxc-form-item>
    <lxc-form-item
      :label="t('dtwins.form.description.geolocation.attributes.address')"
      read-only
    >
      <lxc-input
        :value="dtwin?.attributes?.address ?? NO_CONTENT"
        type="text"
      />
    </lxc-form-item>
    <lxc-form-item
      :label="t('dtwins.form.description.geolocation.attributes.status')"
      read-only
    >
      <lxc-input v-model="latLongRepresentation" type="text" />
    </lxc-form-item>
    <lxc-button
      :title="t('button.validate')"
      @click="router.push({ name: PATH_DTWINS_ID, params: { id: dtwin.uid } })"
    >
      <div class="flex items-center">
        <span>{{
          t("dtwins.cartography.deviceMapTile.button.seeDevice")
        }}</span>
        <i-lxc-arrow-right-circle
          class="ml-2"
          width="1.25rem"
          height="1.25rem"
        />
      </div>
    </lxc-button>
  </div>
</template>
